import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'design-system';
import { makeStyles } from 'design-system/product/theme';
import { CAPTION_PLACEHOLDER } from 'react-scale/dashboard.scale.com/components/SelfServe/Instructions/ExampleLabelModal';
import _ from 'lodash';

interface StaticAssetViewerModalProps {
  modalOpen: boolean;
  setModalOpen: (arg: boolean) => void;
  staticAssetAttachment?: any;
  staticAssetAttachmentType?: any;
  initialCaption?: string;
  submitCaption?: (caption: string) => void;
}

const StaticAssetViewerModal: React.FC<StaticAssetViewerModalProps> = ({
  modalOpen,
  setModalOpen,
  staticAssetAttachment,
  staticAssetAttachmentType,
  initialCaption,
  submitCaption,
}) => {
  const classes = useStyles();

  const close = (event?: any) => {
    // prevent ESC from closing modal
    if (event?.type === 'keydown' && event?.keyCode === 27) return;
    setModalOpen(false);
    if (!_.isNil(captionVal) && submitCaption) {
      submitCaption(captionVal);
    }
  };

  const renderDataView = (asset: string, assetType: string) => {
    if (!asset || !assetType) {
      return <div className={classes.text}>Error loading example.</div>;
    }

    // assume unprocessed is image for now
    if (assetType === 'image' || assetType === 'unprocessed') {
      return <img className={classes.img} src={asset} crossOrigin="anonymous" />;
    } else if (assetType === 'text') {
      return <div className={classes.text}>{asset}</div>;
    } else if (assetType === 'pdf') {
      return <object className={classes.pdf} data={asset}></object>;
    } else if (assetType === 'audio' || assetType === 'transcription_audio') {
      return <audio className={classes.audio} src={asset} controls />;
    } else if (assetType === 'website') {
      return <div className={classes.website}>{asset}</div>;
    } else if (assetType === 'video') {
      return <video className={classes.video} src={asset}></video>;
    } else {
      return null;
    }
  };

  const [captionVal, setCaptionVal] = useState<string | undefined>(initialCaption);

  return (
    <Dialog
      classes={{ root: classes.zIndexLow, paper: classes.dialog }}
      maxWidth="lg"
      open={modalOpen}
      onClose={close}
    >
      <DialogTitle>
        <div className={classes.title}>
          Example{' '}
          <span className={classes.closeButton} onClick={close}>
            &#10005;
          </span>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className={classes.component}>
          {renderDataView(staticAssetAttachment, staticAssetAttachmentType)}
        </div>
      </DialogContent>
      <DialogActions>
        {submitCaption ? (
          <div className={classes.captionContainer}>
            <p className={classes.captionHeader}>Caption</p>
            <input
              value={captionVal}
              placeholder={CAPTION_PLACEHOLDER}
              className={`${classes.caption} ${classes.editableCaption}`}
              onFocus={e => (e.target.placeholder = '')}
              onBlur={e => (e.target.placeholder = CAPTION_PLACEHOLDER)}
              onChange={e => {
                e.preventDefault();
                setCaptionVal(e.target.value);
              }}
            />
          </div>
        ) : (
          <div className={classes.caption}>{captionVal}</div>
        )}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  component: {
    height: '540px',
  },
  dialog: {
    width: '100%',
    overflow: 'hidden',
    margin: '3% auto 20px',
  },
  zIndexHigh: {
    zIndex: '2000 !important' as any,
  },
  zIndexLow: {
    zIndex: '110 !important' as any,
  },
  title: {
    textAlign: 'center',
  },

  closeButton: {
    position: 'absolute',
    right: 20,
    top: 15,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  img: {
    objectFit: 'contain',
    width: '100%',
    height: '100%',
  },
  pdf: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  audio: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
  },
  video: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
    padding: '2px',
    boxSizing: 'border-box',
  },
  website: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
  },
  text: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
  },
  caption: {
    opacity: 0.9,
    marginTop: 12,
    textAlign: 'center',
    border: 0,
    fontSize: '16px',
    width: '100%',
  },
  editableCaption: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginTop: '0px',
  },
  captionContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  captionHeader: {
    width: '100%',
    lineHeight: 1,
    color: 'darkslategray',
    marginBottom: '8px',
  },
});

export default StaticAssetViewerModal;
